import { useDataProvider, useNotify } from 'react-admin';
import { useEffect, useState } from "react";
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AuthNotification from '../Commons/AuthNotification';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import InternalMenu from '../Commons/InternalMenu';
import AnticipoPicker from '../Commons/AnticipoPicker';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// Forma de Pago (codigo_tipo_mov de sp_cbliquida_facturas): no existe un SP que liste este
// catalogo por nombre -- no viene de gecatalogosdominios (ese mecanismo es para catalogos
// genericos de la app vieja, ver el comentario de InvoiceTypes en ProviderInvoiceCreate.jsx),
// sino de un enumerado fijo del modulo de Cuentas Bancarias/Pagos. Los codigos se confirmaron
// leyendo sp_lista_cbtabanxmoneda (las columnas num_solcheq/num_solnd que arma a partir de
// cbmaesolicitud usan codigo_tipo_mov=1 para "solicitud de cheque" y =3 para "solicitud de
// nota de debito").
// Las keys van como string (no numero): el backend (SettleInvoiceModel.PaymentMethod, en
// Providersapi.cs) espera un string -- mismo criterio que el resto de selects de la app,
// cuyo <select> onChange siempre manda e.target.value (string). Si la key fuera numerica,
// el valor por default de abajo (paymentMethod, sin que el usuario toque el combo) viajaria
// como numero JSON y el deserializador del API lo rechaza.
const PaymentMethods = [
    { key: '1', value: 'Cheque' },
    { key: '3', value: 'Nota de Débito' },
];

// Detalle de UNA factura por pagar. Reutiliza el mismo endpoint/SP que
// ProviderInvoiceShow.jsx (provider/invoice/{id} -> sp_devuelve_factura): el correlativo
// que trae cada fila de ProviderPayableInvoicesList (sp_lista_cbfactura_por_pagar) es el
// mismo cbfactura.correlativo que espera sp_devuelve_factura, asi que no hace falta un
// endpoint nuevo. Layout identico al de ProviderInvoiceShow (mismos campos, misma
// distribucion col-3), agregando ademas el detalle de IVA e ISR que aquella pantalla
// carga pero no llega a mostrar. A diferencia de esa pantalla, aca no hay acciones de
// clasificar/gastos contables/integracion, pero si el evento de liquidacion (pago) de la
// factura (sp_cbliquida_facturas, ver handleSettleInvoice mas abajo) y el de revertir su
// registro (sp_cbreviertefacturasregistradas, ver handleRevertRegistration -- el inverso de
// "Registrar" en ProviderInvoiceShow.jsx, que ahi mismo se documenta como irreversible desde
// esa pantalla): una factura por pagar ya esta registrada y clasificada por definicion (asi
// filtra sp_lista_cbfactura_por_pagar), lo que le falta es que le apliquen pagos, o bien que
// se revierta su registro si se cargo con algun error.
const ProviderPayableInvoiceShow = () => {
    const { id } = useParams();
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): sin este estado la ventana se veia en blanco
    // (solo el breadcrumb) mientras se traian los permisos y el detalle de la factura.
    const [initialLoading, setInitialLoading] = useState(true);
    // providerCode (a diferencia de providerName, que si se muestra) solo hace falta para
    // poder regresar al listado de Facturas por Pagar de ESTE proveedor tras liquidar (ver
    // handleSettleInvoice), mismo patron que handleRedirectInvoices en ProviderInvoiceShow.jsx.
    const [providerCode, setProviderCode] = useState('');
    const [providerName, setProviderName] = useState('');
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [series, setSeries] = useState('');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [registrationDate, setRegistrationDate] = useState('');
    const [currencyName, setCurrencyName] = useState('');
    const [referenceRate, setReferenceRate] = useState('');
    const [invoiceAmount, setInvoiceAmount] = useState('');
    const [paidAmount, setPaidAmount] = useState('');
    const [balance, setBalance] = useState('');
    // Valor numerico crudo del saldo pendiente (balance de arriba ya viene formateado para
    // mostrar) -- lo necesita handleSettleInvoice para decidir si un pago es total o
    // parcial. Arranca en null (todavia no cargo la factura).
    const [balanceValue, setBalanceValue] = useState(null);
    const [vatAmount, setVatAmount] = useState('');
    const [retainedVat, setRetainedVat] = useState('');
    const [vatToRetain, setVatToRetain] = useState('');
    const [retainedISR, setRetainedISR] = useState('');
    const [isrToRetain, setIsrToRetain] = useState('');
    const [ducaReference, setDucaReference] = useState('');
    const [ducaNumber, setDucaNumber] = useState('');
    const [importDescription, setImportDescription] = useState('');
    const [daiAmount, setDaiAmount] = useState('');

    // Liquidacion de pago (sp_cbliquida_facturas). Forma de Pago es una lista fija (ver
    // PaymentMethods arriba); Moneda reutiliza el mismo catalogo general de monedas que ya
    // usa ProviderInvoiceCreate.jsx; Cuenta de Pago sale de provider/bankaccounts
    // (sp_lista_cbtaban_simple, ya usado en ProvidersCreate.jsx), filtrada en memoria a las
    // cuentas cuya moneda coincida con la Moneda elegida -- ese catalogo no tiene un modo
    // "por moneda" propio expuesto todavia, pero ya trae el codigo de moneda por cuenta, asi
    // que alcanza con filtrar del lado del cliente.
    // Se inicializa en '1' (Cheque, como string -- ver comentario de PaymentMethods arriba),
    // no en '': el <select> de InputFieldCustom marca "selected" comparando value ===
    // item.key, pero si ningun option matchea (value='' no matchea las keys de
    // PaymentMethods) el navegador de todos modos muestra seleccionada la PRIMERA opcion por
    // comportamiento nativo del <select> -- sin disparar ningun onChange. Eso dejaba el
    // estado de React en '' aunque en pantalla ya se viera "Cheque" elegido, y la validacion
    // de handleSettleInvoice lo rechazaba como vacio. Arrancar en '1' (que ademas coincide
    // con el default de @codigo_tipo_mov en el propio SP) mantiene sincronizados lo que se ve
    // y lo que se manda.
    const [paymentMethod, setPaymentMethod] = useState('1');
    // Numero de documento de la Nota de Debito (ver comentario de PaymentMethods arriba y de
    // numero_documento en Providersapi.cs/SettleInvoice): solo aplica y es obligatorio cuando
    // paymentMethod = '3'. Para Cheque el campo queda deshabilitado (ver mas abajo) porque el
    // numero de documento del cheque lo asigna el propio sistema al imprimirlo, no se captura
    // aca.
    const [documentNumber, setDocumentNumber] = useState('');
    const [currency, setCurrency] = useState('');
    const [currencies, setCurrencies] = useState([]);
    const [paymentAccount, setPaymentAccount] = useState('');
    const [bankAccounts, setBankAccounts] = useState([]);
    const [amount, setAmount] = useState('');
    const [settling, setSettling] = useState(false);
    const [reverting, setReverting] = useState(false);
    // TODAS las acciones/movimientos aplicados a esta factura (anticipos, cheques, notas de
    // credito/debito, caja chica, impuesto retenido, adquiriente, etc.) -- ver
    // loadInvoiceMovements/GET provider/invoice/{id}/movements (provider_invoice_movements_get,
    // Providersapi.cs -> sp_lista_cbfacturacionamortizacion, sin filtrar por tipo). A pedido
    // del usuario, esta pantalla debe mostrar la lista completa que trae ese SP, no solo
    // Anticipos. Un Anticipo en particular ya NO se "elige" para mandarlo junto con
    // "Liquidar": se reserva de inmediato al elegirlo en AnticipoPicker.jsx (POST
    // .../advance-assignment), y esta lista es la unica fuente de verdad de que anticipos
    // estan aplicados -- pueden ser varios, la estructura de cbfacturacionamortizacion no
    // limita a uno solo por factura (el unico tope es que la suma no supere el monto de la
    // factura, lo valida el propio trigger). Los Anticipos que sigan en estado 'I' se pueden
    // liberar a mano (ver handleCancelAdvanceAssignment) si no corresponden o si se quiere
    // deshacer la eleccion -- el resto de los tipos de movimiento son de solo lectura aca (no
    // hay endpoint que los cancele desde esta pantalla).
    const [invoiceMovements, setInvoiceMovements] = useState([]);
    const [cancellingAdvanceId, setCancellingAdvanceId] = useState(null);

    // Extraido del useEffect para poder reutilizarlo despues de liquidar un pago (hay que
    // releer Abonado/Saldo, que sp_cbliquida_facturas actualiza) sin duplicar esta carga.
    const loadInvoice = async () => {
        try {
            const authdata = { data: hasPermissions('ProviderInvoice', 'read') };
            if (authdata.data.length > 0) {
                setAuth(authdata.data[0]);
                setOpenAuth(!authdata.data[0]);

                if (authdata.data[0]) {
                    const { data } = await dataProvider.getOne('provider/invoice', { id });

                    historyTracker.addPath(location.pathname, data.invoiceNumber ? `Factura ${data.invoiceNumber}` : 'Factura', 5);

                    setProviderCode(data.providerCode?.toString());
                    setProviderName(data.providerName);
                    setInvoiceNumber(data.invoiceNumber);
                    setSeries(data.series);
                    setInvoiceDate(formatDate(data.invoiceDate));
                    setPaymentDate(formatDate(data.paymentDate));
                    setRegistrationDate(formatDate(data.registrationDate));
                    setCurrencyName(data.currencyName ? `${data.currencyName}${data.currencySymbol ? ` (${data.currencySymbol})` : ''}` : '');
                    setReferenceRate(data.referenceRate?.toString());
                    setInvoiceAmount(formatMoney(data.invoiceAmount));
                    setPaidAmount(formatMoney(data.paidAmount));
                    setBalance(formatMoney(data.balance));
                    setBalanceValue(data.balance !== null && data.balance !== undefined ? parseFloat(data.balance) : null);
                    setVatAmount(formatMoney(data.vatAmount));
                    setRetainedVat(formatMoney(data.retainedVat));
                    setVatToRetain(formatMoney(data.vatToRetain));
                    setRetainedISR(formatMoney(data.retainedISR));
                    setIsrToRetain(formatMoney(data.isrToRetain));
                    setDucaReference(data.ducaReference);
                    setDucaNumber(data.ducaNumber);
                    setImportDescription(data.importDescription);
                    setDaiAmount(data.importCorrelative ? formatMoney(data.daiAmount) : '');

                    // Moneda de pago: se preselecciona con la misma moneda de la factura, que
                    // es la unica que hoy valida el backend (ver comentario de
                    // handleSettleInvoice). Solo se pisa el default la primera vez (si el
                    // usuario ya habia elegido algo, una recarga tras liquidar no deberia
                    // resetearselo).
                    setCurrency((prev) => prev || data.currencyCode?.toString() || '');

                    const [currenciesData, bankAccountsData] = await Promise.all([
                        dataProvider.getList('currency', { filter: {}, pagination: {}, sort: {} }),
                        dataProvider.getList('provider/bankaccounts', { filter: {}, pagination: {}, sort: {} }),
                    ]);
                    setCurrencies(currenciesData.data.map(item => ({ key: item.id, value: item.name })));
                    setBankAccounts(bankAccountsData.data);

                    // Cuenta de Pago por defecto del Proveedor: cada Proveedor tiene su propia
                    // Cuenta Bancaria asignada (accountCode, el mismo campo "Cuenta Bancaria" de
                    // ProvidersCreate.jsx/ProvidersShow.jsx -- sale de sp_devuelve_proveedor, que
                    // trae ese dato via INNER JOIN contra cbtaban/cbbanco). Si esa cuenta esta
                    // entre las disponibles para la moneda de ESTA factura (mismo filtro que ya
                    // arma "paymentAccounts" mas abajo), se preselecciona aca en vez de dejar
                    // siempre "Seleccione una cuenta" y obligar a elegirla a mano en cada pago.
                    // Va en su propio try/catch: que este llamado puntual falle (sin permiso
                    // read:Provider, o un proveedor sin cuenta asignada) no debe tumbar la carga
                    // del resto de la factura.
                    try {
                        const providerData = await dataProvider.getOne('provider', { id: data.providerCode });
                        const defaultAccountCode = providerData?.data?.accountCode;
                        if (defaultAccountCode) {
                            const invoiceCurrency = data.currencyCode?.toString() || '';
                            const isAvailable = bankAccountsData.data.some((item) =>
                                String(item.accountCode) === String(defaultAccountCode) && String(item.currency) === invoiceCurrency
                            );
                            if (isAvailable) {
                                // Igual que con "currency" arriba: solo pisa el default la primera
                                // vez -- si el usuario ya habia elegido otra cuenta, una recarga
                                // tras un pago parcial no deberia pisarsela.
                                setPaymentAccount((prev) => prev || String(defaultAccountCode));
                            }
                        }
                    } catch (error) {
                        console.log(error);
                    }
                }
            } else {
                setAuth(false);
                setOpenAuth(true);
            }
        }
        catch (error) {
            console.log(error);
        }
        finally {
            setInitialLoading(false);
        }
    };

    // Ver el comentario de invoiceMovements (arriba): consulta
    // provider_invoice_movements_get (Providersapi.cs), separado de loadInvoice para poder
    // recargar solo esta lista (sin releer toda la factura) despues de aplicar o liberar un
    // anticipo.
    //
    // dataProvider.getList (no getOne): el endpoint devuelve un ARREGLO de movimientos, y
    // react-admin valida en el propio navegador que la respuesta de getOne sea UN registro
    // con "id" ("ra.notification.data_provider_error" / "The response to 'getOne' must be
    // like { data: { id: 123, ... } }") -- con un arreglo, la promesa nunca resuelve del lado
    // exitoso y el catch de abajo se dispara siempre, aunque el backend haya respondido bien.
    // getList si espera un arreglo (ver el ListResultWithRange que ahora arma la respuesta en
    // Providersapi.cs). filter/pagination/sort van vacios porque este endpoint no pagina de
    // verdad (mismo patron que otras listas chicas de esta misma pantalla, ver loadInvoice).
    const loadInvoiceMovements = async () => {
        try {
            const { data } = await dataProvider.getList(`provider/invoice/${id}/movements`, { filter: {}, pagination: {}, sort: {} });
            setInvoiceMovements(Array.isArray(data) ? data : []);
        } catch (error) {
            // Si esto vuelve a fallar (404 por un backend viejo sin este endpoint, 403 por
            // permisos, 500, etc.) ahora se notifica en vez de quedar en silencio (antes solo
            // console.log), para poder diagnosticarlo desde la propia pantalla en vez de tener
            // que abrir las DevTools.
            console.error('No se pudo cargar la lista de movimientos aplicados a la factura:', error);
            notify(error?.message || 'No se pudo cargar la lista de movimientos aplicados a la factura.', { type: 'warning' });
        }
    };

    useEffect(() => {
        loadInvoice();
        loadInvoiceMovements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname, id]);

    // Cuenta de Pago filtrada a la moneda elegida (ver comentario de bankAccounts arriba).
    // String(...) en ambos lados porque currency (key del select) es string y
    // item.currency llega como numero crudo desde el API. Arranca con una opcion en blanco
    // real (mismo criterio que "Ninguno" en ProvidersCreate.jsx): a diferencia de Forma de
    // Pago, aca si se quiere forzar una eleccion explicita, y sin esta opcion en blanco el
    // <select> mostraria la primera cuenta ya "elegida" visualmente sin que paymentAccount
    // se haya actualizado (mismo problema que se corrigio en paymentMethod, arriba).
    const paymentAccounts = [
        { key: '', value: 'Seleccione una cuenta' },
        ...bankAccounts
            .filter((item) => String(item.currency) === String(currency))
            .map((item) => ({ key: item.accountCode, value: `${item.accountNumber} - ${item.accountName} (${item.bankName})` })),
    ];

    const handleCurrencyChange = (value) => {
        setCurrency(value);
        // Las cuentas de pago dependen de la moneda: si se cambia la moneda, la cuenta ya
        // elegida (si la habia) puede ya no aplicar, asi que se limpia para no dejar
        // seleccionada una cuenta que no corresponde.
        setPaymentAccount('');
    };

    // El numero de documento solo aplica a Nota de Debito ('3'): si se cambia a Cheque se
    // limpia, tanto para no dejarlo pre-cargado si se vuelve a elegir Nota de Debito por
    // error, como para no mandarlo de mas en handleSettleInvoice.
    const handlePaymentMethodChange = (value) => {
        setPaymentMethod(value);
        if (value !== '3') {
            setDocumentNumber('');
        }
    };

    // Mismo patron que handleRedirectInvoices en ProviderInvoiceShow.jsx/ProvidersShow.jsx: el
    // codigo/nombre del proveedor viajan en la propia query string (no solo en location.state)
    // porque ProviderPayableInvoicesList usa <List syncWithLocation>, que reescribe la URL
    // apenas monta y ahi se pierde el state.
    const handleRedirectPayableInvoices = () => {
        navigate({
            pathname: '/provider/invoicetopay',
            search: `?filter=${encodeURIComponent(JSON.stringify({ provider: providerCode }))}&providerName=${encodeURIComponent(providerName || '')}`,
        }, { state: { providerName, providerCode } });
    };

    // Ver el comentario de invoiceMovements (arriba): se llama apenas AnticipoPicker.jsx
    // termina de reservar un anticipo (ya lo hizo contra el backend, esto solo refresca lo
    // que muestra esta pantalla) -- tanto el Saldo (loadInvoice, que ya viene neto del
    // anticipo recien reservado) como la lista de movimientos aplicados.
    const handleAdvanceApplied = async () => {
        await Promise.all([loadInvoice(), loadInvoiceMovements()]);
    };

    const handleSettleInvoice = async () => {
        // "cashAmount" normaliza el campo "amount" a numero -- puede llegar vacio si el Saldo
        // ya esta en 0 (ver el chequeo de abajo), asi que se trata ese caso como 0 en vez de
        // NaN, para no repetir un parseo distinto en cada chequeo de esta funcion.
        const cashAmount = amount ? (parseFloat(amount.toString().replace(/[^0-9.-]/g, '')) || 0) : 0;
        // "Monto a Aplicar" (efectivo/cheque/ND) es obligatorio salvo que el Saldo ya este en
        // 0 -- balanceValue ya viene neto de cualquier Anticipo reservado previamente desde
        // AnticipoPicker.jsx (confirmado con el usuario experto de la base de datos:
        // sp_devuelve_factura calcula bien el Saldo, no hace falta descontar nada mas aca).
        const balanceAlreadyCovered = balanceValue !== null && balanceValue <= 0.005;
        if (!paymentMethod || !currency || !paymentAccount || (!balanceAlreadyCovered && cashAmount <= 0)) {
            notify('Debe indicar forma de pago, moneda, cuenta de pago y un monto a aplicar válido.', { type: 'warning' });
            return;
        }

        // setAmountGuarded (ver mas abajo) ya evita que el campo llegue a superar el Saldo
        // mientras se escribe, pero el Saldo puede bajar despues de eso (ej. otro anticipo
        // reservado mientras esta ventana seguia abierta) -- este chequeo es la ultima
        // barrera que de verdad bloquea el envio si igual se hace click en "Liquidar" con
        // un monto que ya quedo invalido.
        if (amountExceedsBalance) {
            notify('El monto a aplicar no puede ser mayor al saldo pendiente.', { type: 'warning' });
            return;
        }

        // Nota de Debito ('3'): el numero de documento lo pone el banco, asi que aca es
        // obligatorio (a diferencia de Cheque, donde el numero lo asigna el sistema al
        // imprimir). Mismo criterio de validacion que usa el backend (ver SettleInvoice en
        // Providersapi.cs).
        if (paymentMethod === '3' && (!documentNumber || !/^[0-9]+$/.test(documentNumber.toString().trim()))) {
            notify('Debe indicar el número de documento de la Nota de Débito.', { type: 'warning' });
            return;
        }

        // Se considera "pago total" si lo aplicado en efectivo/cheque/ND (cashAmount) cubre,
        // con la misma tolerancia de centavos que ya usa el resto de la app (ej. canRegister
        // en ProviderInvoiceShow.jsx), el Saldo pendiente (que ya tiene descontado cualquier
        // Anticipo reservado -- ver balanceAlreadyCovered arriba). Si es total, la factura
        // sale de Facturas por Pagar (sp_lista_cbfactura_por_pagar solo lista saldo pendiente
        // > 0) y no tiene caso quedarse aca; si es parcial, la factura sigue con saldo, asi
        // que conviene quedarse en esta misma vista por si se le quiere seguir aplicando mas
        // pagos.
        const isFullPayment = balanceValue === null || cashAmount >= balanceValue - 0.005;

        setSettling(true);
        try {
            // Ya NO se manda ningun Anticipo aca (ver el comentario de handleAdvanceApplied,
            // arriba): eso ya se resolvio antes, en el momento de "Elegir" dentro del picker.
            // sp_cbliquida_facturas (SettleInvoice, Providersapi.cs) procesa por su cuenta
            // TODOS los anticipos que ya esten asignados a esta factura.
            await dataProvider.create(`provider/invoice/${id}/settle`, {
                data: {
                    paymentMethod,
                    paymentAccount,
                    currency,
                    amount: cashAmount.toString(),
                    documentNumber: paymentMethod === '3' ? documentNumber.toString().trim() : null,
                },
            });
            notify('Pago aplicado correctamente.', { type: 'success' });
            if (isFullPayment) {
                // No hace falta volver a poner settling en false ni releer la factura: se
                // abandona esta pantalla y se vuelve al listado de Facturas por Pagar del
                // mismo proveedor (la factura ya quedo completamente liquidada, asi que sale
                // de ese listado).
                handleRedirectPayableInvoices();
            } else {
                // Pago parcial: la factura sigue en Facturas por Pagar con el resto del
                // saldo, asi que se recarga esta misma pantalla (Abonado/Saldo actualizados,
                // y los anticipos que se acaban de liquidar pasan de "Pendiente" a
                // "Liquidado" en el listado de aplicados) en vez de salir, y se limpia el
                // monto ya aplicado para no dejarlo cargado por si se quiere aplicar otro
                // pago a continuacion.
                setAmount('');
                setDocumentNumber('');
                await Promise.all([loadInvoice(), loadInvoiceMovements()]);
                setSettling(false);
            }
        } catch (error) {
            notify(error?.message || 'No se pudo aplicar el pago.', { type: 'error' });
            setSettling(false);
        }
    };

    // Libera una reserva de Anticipo que quedo asignada a esta factura sin liquidar (estado
    // 'I' en invoiceMovements, arriba) -- llama a provider_invoice_advance_assignment_cancel_post
    // (Providersapi.cs), que ejecuta sp_Baja_cbfacturacionamortizacion (pone estado='E' en
    // esa fila de cbfacturacionamortizacion). El trigger de esa tabla revierte solo, a
    // partir de ese cambio de estado, tanto los montos de esta factura
    // (monto_anticipo/monto_anticipoxliquidar/monto_abonado) como la disponibilidad del
    // anticipo original (monto_por_liquidar) -- por eso despues de liberar se recargan tanto
    // la factura como este listado. Solo aplica a Anticipos (tipo='A'); el backend rechaza
    // cualquier otro tipo, y el boton de "Liberar reserva" en el JSX ya no se ofrece para
    // otros tipos de movimiento.
    const handleCancelAdvanceAssignment = async (correlativo) => {
        setCancellingAdvanceId(correlativo);
        try {
            await dataProvider.create(`provider/invoice/${id}/advance-assignment/${correlativo}/cancel`, { data: {} });
            notify('Reserva de anticipo liberada correctamente.', { type: 'success' });
            await Promise.all([loadInvoice(), loadInvoiceMovements()]);
        } catch (error) {
            notify(error?.message || 'No se pudo liberar la reserva del anticipo.', { type: 'error' });
        } finally {
            setCancellingAdvanceId(null);
        }
    };

    // sp_cbreviertefacturasregistradas: el inverso de "Registrar" (ver comentario del
    // backend en RevertInvoiceRegistration/Providersapi.cs) -- regresa esta factura de
    // 'Registrada' a 'Sin Registrar', revirtiendo la partida contable y la integracion de
    // activos/inventarios si aplica. El propio SP rechaza el reverso si la factura ya tiene
    // pagos (liquidaciones) aplicados, asi que ese mensaje (y cualquier otro que devuelva el
    // SP) llega tal cual via notify, igual que en handleSettleInvoice.
    const handleRevertRegistration = async () => {
        setReverting(true);
        try {
            await dataProvider.create(`provider/invoice/${id}/revert`, { data: {} });
            notify('Registro revertido correctamente.', { type: 'success' });
            // Igual que al liquidar: la factura deja de estar "registrada", asi que ya no
            // corresponde que siga en Facturas por Pagar -- se vuelve al listado del mismo
            // proveedor en vez de recargar esta pantalla.
            handleRedirectPayableInvoices();
        } catch (error) {
            notify(error?.message || 'No se pudo revertir el registro de la factura.', { type: 'error' });
            setReverting(false);
        }
    };

    const handleRedirectProviderList = () => {
        navigate(`/provider`);
    };

    const [openProviders, setOpenProviders] = useState(true);

    const handleClickOpenProviders = () => {
        setOpenProviders(!openProviders);
    };

    const menus = [{
        icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
        open: openProviders,
        openAction: handleClickOpenProviders,
        actions: [
            { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
        ]
    }];

    const hasImportInfo = !!(ducaReference || ducaNumber || importDescription || daiAmount);
    // Bloqueo de pantalla compartido entre las dos acciones que mutan la factura
    // (liquidar/revertir registro): solo una puede estar en curso a la vez, y cualquiera de
    // las dos debe mostrar el mismo spinner y atenuar el formulario.
    const busy = settling || reverting || cancellingAdvanceId !== null;

    // "Monto a Aplicar" (efectivo/cheque/ND) no puede superar el Saldo pendiente. Ya no hace
    // falta descontarle nada a mano: desde que el Anticipo se reserva al elegirlo (no junto
    // con "Liquidar"), balanceValue -- tal como lo calcula sp_devuelve_factura -- ya viene
    // neto de cualquier anticipo ya aplicado a esta factura (confirmado con el usuario
    // experto de la base de datos, ver el analisis "Anticipos vs Facturas por Pagar" del
    // proyecto "Hawk Restructuracion"). La tolerancia de 0.005 es la misma que ya usa
    // "isFullPayment" mas arriba para no rechazar por un redondeo de centavos.
    //
    // Antes esto solo se usaba para pintar el campo en rojo y mostrar un texto aparte debajo
    // (ver el chequeo repetido en handleSettleInvoice) -- a pedido, ahora se avisa igual que
    // el resto de los errores de monto de la aplicacion (ver setAmountGuarded, mismo patron
    // que setAmountGuarded en AccountingExpenseCreate.jsx/InventoryIntegrationCreate.jsx):
    // una notificacion, y el campo se ajusta solo al maximo permitido en vez de quedar en un
    // valor invalido. amountExceedsBalance se sigue calculando igual, ya no para pintar nada
    // en el formulario sino solo como ultima barrera en handleSettleInvoice (ver ahi).
    const parsedAmountValue = amount ? parseFloat(amount.toString().replace(/[^0-9.-]/g, '')) : NaN;
    const amountExceedsBalance = balanceValue !== null && !isNaN(parsedAmountValue) && parsedAmountValue > balanceValue + 0.005;

    // Intercepta cada llamada a setValue que hace InputFieldCustom para "Monto a Aplicar" (se
    // llama dos veces por cada tecla cuando "money" esta activo -- una con el texto crudo y
    // otra ~500ms despues ya reformateada, ver el mismo comentario en setAmountGuarded de
    // AccountingExpenseCreate.jsx): si lo escrito supera el Saldo pendiente, en vez de dejar
    // que quede en un valor invalido (antes se marcaba en rojo con un texto debajo), avisa con
    // una notificacion y ajusta el campo al maximo permitido (el propio Saldo).
    const setAmountGuarded = (rawValue) => {
        const enteredAmount = parseFloat(String(rawValue).replace(/[^0-9.-]/g, '')) || 0;
        if (balanceValue !== null && enteredAmount > balanceValue + 0.005) {
            notify(`El monto (Q ${enteredAmount.toFixed(2)}) supera el saldo pendiente (Q ${balanceValue.toFixed(2)}). Se ajustó al máximo disponible.`, { type: 'warning' });
            setAmount(Math.max(balanceValue, 0).toFixed(2));
            return;
        }
        setAmount(rawValue);
    };

    return (
        <div class="container">
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que ya se usa para liquidar/revertir (mas abajo,
                // controlado por "busy"), pero aqui cubre la espera de auth + detalle de la
                // factura, que antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                /* Sin recorte de paddingBottom: a diferencia de las pantallas de "Crear"
                   (formularios cortos, donde se recorta a 0.1em para no dejar demasiado aire
                   abajo), esta es una pantalla de "Ver" como ProviderInvoiceShow.jsx y usa el
                   padding-bottom de 2em por defecto de la clase "edit" (index.css) para que el
                   menu inferior no quede pegado al borde de la division. */
                <div class="edit">
                    {!isSmall ? (
                        // Mismo patron de "Loading" que ProviderInvoiceShow.jsx (handleRegisterInvoice)
                        // y las pantallas de Crear: mientras se liquida el pago o se revierte el
                        // registro, se muestra el spinner centrado y se atenua el resto del
                        // formulario -- disabled en BottomToolBar (mas abajo) ya bloquea los
                        // botones, pero esto ademas bloquea visualmente los campos para que no se
                        // puedan seguir editando mientras la operacion esta en curso.
                        <div class="Loading" style={{ display: busy ? 'block' : 'none' }}>
                            <CircularProgress size="15rem" />
                        </div>
                    ) : null}
                    {!isSmall ? (
                        <div class="container" style={{ opacity: busy ? 0.3 : 1 }}>
                            <div class="row">
                                <InputFieldCustom readOnly value={providerName} setValue={setProviderName} col="col-3" width="95%" label="Proveedor" type="text" />
                                <InputFieldCustom readOnly value={invoiceNumber} setValue={setInvoiceNumber} col="col-3" width="95%" label="Número" type="text" />
                                <InputFieldCustom readOnly value={series} setValue={setSeries} col="col-3" width="95%" label="Serie" type="text" />
                                <InputFieldCustom readOnly value={invoiceDate} setValue={setInvoiceDate} col="col-3" width="95%" label="Fecha de Factura" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={paymentDate} setValue={setPaymentDate} col="col-3" width="95%" label="Fecha de Pago" type="text" />
                                <InputFieldCustom readOnly value={registrationDate} setValue={setRegistrationDate} col="col-3" width="95%" label="Fecha de Registro" type="text" />
                                <InputFieldCustom readOnly value={currencyName} setValue={setCurrencyName} col="col-3" width="95%" label="Moneda" type="text" />
                                <InputFieldCustom readOnly value={referenceRate} setValue={setReferenceRate} col="col-3" width="95%" label="Tasa de Referencia" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={invoiceAmount} setValue={setInvoiceAmount} col="col-3" width="95%" label="Monto (Q)" type="text" />
                                <InputFieldCustom readOnly value={paidAmount} setValue={setPaidAmount} col="col-3" width="95%" label="Abonado (Q)" type="text" />
                                <InputFieldCustom readOnly value={balance} setValue={setBalance} col="col-3" width="95%" label="Saldo (Q)" type="text" />
                                <InputFieldCustom readOnly value={vatAmount} setValue={setVatAmount} col="col-3" width="95%" label="Monto IVA (Q)" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={retainedVat} setValue={setRetainedVat} col="col-3" width="95%" label="IVA Retenido (Q)" type="text" />
                                <InputFieldCustom readOnly value={vatToRetain} setValue={setVatToRetain} col="col-3" width="95%" label="IVA por Retener (Q)" type="text" />
                                <InputFieldCustom readOnly value={retainedISR} setValue={setRetainedISR} col="col-3" width="95%" label="ISR Retenido (Q)" type="text" />
                                <InputFieldCustom readOnly value={isrToRetain} setValue={setIsrToRetain} col="col-3" width="95%" label="ISR por Retener (Q)" type="text" />
                            </div>
                            {hasImportInfo ? (
                                <>
                                    <div class="row" style={{ paddingTop: '1.2em' }}>
                                        <div class="col-4">
                                            <label style={Style.Label}>Importación</label>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <InputFieldCustom readOnly value={ducaReference} setValue={setDucaReference} col="col-3" width="95%" label="Referencia DUCA" type="text" />
                                        <InputFieldCustom readOnly value={ducaNumber} setValue={setDucaNumber} col="col-3" width="95%" label="No. DUCA" type="text" />
                                        <InputFieldCustom readOnly value={daiAmount} setValue={setDaiAmount} col="col-3" width="95%" label="Monto DAI (Q)" type="text" />
                                    </div>
                                    <div class="row">
                                        {/* referenceCol="col-3": alinea con la fila de arriba (Referencia
                                            DUCA/No. DUCA/Monto DAI x2), que ahora son 4 campos col-3 -- ver
                                            el comentario de computeTextareaWidth en InputFieldCustom.jsx. */}
                                        <InputFieldCustom readOnly value={importDescription} setValue={setImportDescription} col="col-12" width="95%" label="Descripción" type="text" component="textarea" referenceCol="col-3" />
                                    </div>
                                </>
                            ) : null}
                            <div class="row" style={{ paddingTop: '1.2em' }}>
                                <div class="col-4">
                                    <label style={Style.Label}>Liquidación de Pago</label>
                                </div>
                            </div>
                            <div class="row">
                                <InputFieldCustom col="col-3" width="95%" setValue={handlePaymentMethodChange} value={paymentMethod} options={PaymentMethods} component="select" label="Forma de Pago" />
                                {/* Por ahora solo se puede liquidar en la misma moneda de la factura
                                    (ver comentario de handleSettleInvoice/backend): se preselecciona
                                    con esa moneda al cargar, pero se deja el catalogo completo por si
                                    mas adelante se agrega soporte a tipo de cambio para pagar en otra
                                    moneda. */}
                                <InputFieldCustom col="col-3" width="95%" setValue={handleCurrencyChange} value={currency} options={currencies} component="select" label="Moneda" />
                                <InputFieldCustom col="col-3" width="95%" setValue={setPaymentAccount} value={paymentAccount} options={paymentAccounts} component="select" label="Cuenta de Pago" />
                                <InputFieldCustom value={amount} setValue={setAmountGuarded} col="col-3" width="95%" placeholder="Ingresa el monto" label="Monto a Aplicar (Q) *" required type="text" money />
                            </div>
                            {/* No. de Documento (Nota de Debito): antes quedaba debajo del panel de
                                movimientos, al final de todo el formulario -- a pedido, se sube aca,
                                pegado a la fila de Forma de Pago/Moneda/Cuenta/Monto de la que en
                                realidad depende (solo aplica cuando Forma de Pago = Nota de Debito),
                                en vez de quedar suelto despues de la lista de movimientos. Solo
                                aparece cuando la Forma de Pago es Nota de Debito -- para Cheque no
                                corresponde (el numero lo asigna el sistema al imprimirlo, no se
                                captura aca) y se pidio que directamente no se muestre. Se limpia al
                                cambiar de Forma de Pago (ver handlePaymentMethodChange), asi que no
                                queda un numero de documento cargado de una Nota de Debito anterior si
                                se vuelve a elegir esa opcion. */}
                            {paymentMethod === '3' ? (
                                <div class="row">
                                    <InputFieldCustom value={documentNumber} setValue={setDocumentNumber} col="col-3" width="95%" placeholder="Ingresa el número de documento" label="No. de Documento (ND) *" required type="text" />
                                </div>
                            ) : null}
                            {/* Aplicar Anticipo: a pedido, ya no es un campo de solo lectura con lo
                                "elegido" pendiente de enviar -- ahora la reserva se hace de inmediato
                                al elegir (ver AnticipoPicker.jsx/handleAdvanceApplied), asi que aca
                                solo queda el disparador (la lupa) para abrir el buscador; lo que ya
                                se aplico se ve en el panel de abajo, no en este campo (puede haber
                                mas de un anticipo aplicado a la misma factura). paddingBottom (ademas
                                del paddingTop que ya tenia) para separarla mas del panel de
                                movimientos que sigue debajo -- a pedido, quedaban demasiado pegados. */}
                            <div class="row" style={{ alignItems: 'center', paddingTop: '1.2em', paddingBottom: '1.2em' }}>
                                <div class="col-4" style={{ display: 'flex', alignItems: 'center' }}>
                                    <label style={Style.Label}>Aplicar Anticipo</label>
                                    <AnticipoPicker providerCode={providerCode} invoiceId={id} invoiceBalance={balanceValue} onApplied={handleAdvanceApplied} disabled={busy} />
                                </div>
                            </div>
                            {/* Ver el comentario de invoiceMovements (arriba): TODO lo que trae
                                sp_lista_cbfacturacionamortizacion para esta factura -- Anticipos,
                                Cheques, Notas de Credito/Debito, Caja Chica, Impuesto Retenido,
                                Adquiriente, etc. -- no solo Anticipos. Solo los Anticipos pendientes
                                (tipo='A', estado='I') muestran ademas un boton para liberar la
                                reserva (si no corresponden, o si el usuario se arrepiente antes de
                                Liquidar); el resto de los tipos son de solo lectura aca.
                                A pedido, este panel ya NO se oculta por completo cuando la lista
                                viene vacia (antes, con 0 filas, no se distinguia una factura sin
                                movimientos de una carga que fallo en silencio) -- siempre se
                                dibuja el titulo, y adentro se muestra la lista o un mensaje de
                                vacio segun corresponda. */}
                            <div class="row">
                                <div class="col-12">
                                    <div style={{ border: '1px solid #cccccc', backgroundColor: '#f7f7f7', borderRadius: '4px', padding: '0.8em 1em', marginBottom: '0.5em' }}>
                                        <span style={{ fontFamily: 'Gothic', fontSize: '0.8em', fontWeight: 'bold', color: '#444444' }}>
                                            Movimientos aplicados a esta factura
                                        </span>
                                        {invoiceMovements.length === 0 ? (
                                            <div class="row" style={{ marginTop: '0.4em' }}>
                                                <div class="col-12" style={{ fontFamily: 'Gothic', fontSize: '0.8em', color: '#777777' }}>
                                                    No hay movimientos aplicados a esta factura todavía.
                                                </div>
                                            </div>
                                        ) : invoiceMovements.map((item) => {
                                                const isPending = item.estado === 'I';
                                                const canCancel = item.tipo === 'A' && isPending;
                                                return (
                                                    <div key={item.correlativo} class="row" style={{ alignItems: 'center', marginTop: '0.4em' }}>
                                                        <div class="col-3" style={{ fontFamily: 'Gothic', fontSize: '0.8em', color: '#444444' }}>
                                                            {item.tipoDescripcion}
                                                        </div>
                                                        <div class="col-4" style={{ fontFamily: 'Gothic', fontSize: '0.8em', color: '#444444' }}>
                                                            Q {formatMoney(item.amount)} — {formatDate(item.date)}
                                                        </div>
                                                        <div class="col-2" style={{ fontFamily: 'Gothic', fontSize: '0.75em', color: isPending ? '#8a6300' : '#2e7d32' }}>
                                                            {isPending ? 'Pendiente' : 'Procesado'}
                                                        </div>
                                                        <div class="col-3" style={{ textAlign: 'right' }}>
                                                            {canCancel ? (
                                                                <button
                                                                    type="button"
                                                                    disabled={busy}
                                                                    onClick={() => handleCancelAdvanceAssignment(item.correlativo)}
                                                                    style={{ fontFamily: 'Gothic', fontSize: '0.75em', cursor: busy ? 'default' : 'pointer', border: '1px solid #8a6300', backgroundColor: 'transparent', color: '#8a6300', borderRadius: '3px', padding: '0.2em 0.6em' }}
                                                                >
                                                                    {cancellingAdvanceId === item.correlativo ? 'Liberando...' : 'Liberar reserva'}
                                                                </button>
                                                            ) : null}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            <div style={{ marginTop: '2em' }}>
                                {/* "Revertir Registro" reutiliza el mismo permiso "ProviderInvoice"
                                    que el resto de la pantalla (mismo criterio que "Liquidar" arriba)
                                    -- el propio SP (sp_cbreviertefacturasregistradas) es quien
                                    realmente protege la operacion, rechazandola si la factura no esta
                                    en estado Registrada o si ya tiene pagos aplicados pendientes de
                                    revertir. */}
                                <BottomToolBar disabled={busy} buttons={[{ auth: auth, action: handleSettleInvoice, name: settling ? 'Liquidando...' : 'Liquidar' }, { auth: auth, action: handleRevertRegistration, name: reverting ? 'Revirtiendo...' : 'Revertir' }]} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container">
                        </div>
                    )}
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)
            }
        </div>
    )
}

export default ProviderPayableInvoiceShow;
